import { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import useRole from '../../hooks/useRole';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Stack,
  alpha,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const DEVICE_LABELS = {
  company: 'Firma',
  counter: 'Kasse',
  private: 'Privat',
};
const LEVEL_LABELS = { standard: 'Standard', expanded: 'Erweitert' };

function RoleRightsOverview() {
  const theme = useTheme();
  const deviceLevelColor = useMemo(
    () => (device, level) => {
      const colors = {
        company: { standard: theme.palette.primary.dark, expanded: theme.palette.primary.light },
        counter: { standard: theme.palette.success.dark, expanded: theme.palette.success.light },
        private: { standard: theme.palette.warning.dark, expanded: theme.palette.warning.light },
      };
      return colors[device]?.[level] || theme.palette.grey[600];
    },
    [theme]
  );
  const authCtx = useContext(AuthContext);
  const { getRightsOverview, getRightsOverviewDetail } = useRole();

  if (!authCtx.checkRight('role:read') || !authCtx.checkRight('user:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const [overview, setOverview] = useState({ rightsOverview: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [onlyDangerous, setOnlyDangerous] = useState(false);
  const [selectedRight, setSelectedRight] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRightsOverview()
      .then(data => {
        if (!cancelled) setOverview(data || { rightsOverview: [] });
      })
      .catch(() => {
        if (!cancelled) toast.error('Fehler beim Laden der Rechte-Übersicht');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadDetail = useCallback(right => {
    if (!right) {
      setDetail(null);
      setSelectedRight(null);
      return;
    }
    setSelectedRight(right);
    setDetailLoading(true);
    setDetail(null);
    getRightsOverviewDetail(right)
      .then(data => {
        setDetail(data);
      })
      .catch(() => {
        toast.error('Fehler beim Laden der Detailansicht');
        setDetail(null);
      })
      .finally(() => {
        setDetailLoading(false);
      });
  }, []);

  const filteredRights = useMemo(() => {
    let list = overview.rightsOverview || [];
    if (onlyDangerous) list = list.filter(item => item.dangerous === true);
    const term = (search || '').trim().toLowerCase();
    const filtered = term
      ? list.filter(item => (item.right || '').toLowerCase().includes(term))
      : list;
    return [...filtered].sort((a, b) => (a.right || '').localeCompare(b.right || ''));
  }, [overview.rightsOverview, search, onlyDangerous]);

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 2, maxWidth: 1400, mx: 'auto' }}>
      <Stack direction='row' alignItems='center' gap={1} sx={{ mb: 2 }}>
        <SecurityIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Typography variant='h5' component='h1'>
          Rechte-Übersicht
        </Typography>
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Alle Rechte mit zugehörigen Rollen (Gerät: Firma/Kasse/Privat, Standard/Erweitert). Ein
        Recht auswählen für Details inkl. Nutzer.
      </Typography>

      <Stack direction='row' alignItems='center' flexWrap='wrap' gap={2} sx={{ mb: 2 }}>
        <TextField
          size='small'
          placeholder='Recht suchen (z. B. role:read)'
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon color='action' />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={onlyDangerous}
              onChange={e => setOnlyDangerous(e.target.checked)}
              color='error'
              size='small'
            />
          }
          label='Nur gefährliche Rechte'
        />
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Paper
            variant='outlined'
            sx={{
              flex: '1 1 360px',
              minWidth: 280,
              maxHeight: '70vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant='subtitle2'
              sx={{ px: 2, py: 1.5, bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }}
            >
              Rechte ({filteredRights.length})
            </Typography>
            <List sx={{ overflow: 'auto', flex: 1 }}>
              {filteredRights.map(({ right, roles, dangerous }) => (
                <ListItemButton
                  key={right}
                  selected={selectedRight === right}
                  onClick={() => loadDetail(right)}
                  sx={{
                    py: 0.5,
                    ...(dangerous
                      ? {
                          bgcolor: theme =>
                            alpha(
                              theme.palette.error.main,
                              theme.palette.mode === 'light' ? 0.06 : 0.08
                            ),
                          borderLeft: theme => `3px solid ${theme.palette.error.main}`,
                        }
                      : {}),
                  }}
                >
                  <ListItemText
                    primary={right}
                    secondary={
                      <Stack direction='row' flexWrap='wrap' gap={0.5} sx={{ mt: 0.5 }}>
                        {roles.map(r => (
                          <Chip
                            key={r.roleId}
                            size='small'
                            label={r.readableName || r.name}
                            sx={{
                              fontSize: '0.7rem',
                              maxWidth: 120,
                              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                            }}
                          />
                        ))}
                      </Stack>
                    }
                    primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace' }}
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>

          <Paper
            variant='outlined'
            sx={{
              flex: '1 1 400px',
              minWidth: 320,
              maxHeight: '70vh',
              overflow: 'auto',
              p: 2,
            }}
          >
            {!selectedRight && !detailLoading && (
              <Typography color='text.secondary' variant='body2'>
                Rechts auswählen oder suchen, um Rollen und Nutzer zu sehen.
              </Typography>
            )}
            {detailLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {detail && selectedRight && !detailLoading && (
              <>
                <Stack direction='row' alignItems='center' flexWrap='wrap' gap={1} sx={{ mb: 2 }}>
                  <BadgeIcon sx={{ color: 'primary.main' }} />
                  <Typography variant='h6' component='h2' fontFamily='monospace'>
                    {detail.right}
                  </Typography>
                  {overview.rightsOverview?.find(r => r.right === selectedRight)?.dangerous && (
                    <Chip
                      size='small'
                      label='Gefährliches Recht'
                      color='error'
                      variant='outlined'
                      sx={{ fontWeight: 500 }}
                    />
                  )}
                </Stack>

                <Typography variant='subtitle2' color='primary' sx={{ mt: 2, mb: 1 }}>
                  <GroupIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                  Rollen ({detail.roles?.length ?? 0})
                </Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {(detail.roles || []).map(r => (
                    <Paper
                      key={r.roleId}
                      variant='outlined'
                      sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                    >
                      <Typography variant='body2' fontWeight='medium'>
                        {r.readableName || r.name}
                      </Typography>
                      <Stack direction='row' flexWrap='wrap' gap={0.5} sx={{ mt: 0.5 }}>
                        {(r.devices || []).map(({ device, level }) => (
                          <Chip
                            key={`${device}-${level}`}
                            size='small'
                            label={`${DEVICE_LABELS[device] || device} · ${LEVEL_LABELS[level] || level}`}
                            sx={{
                              bgcolor: alpha(deviceLevelColor(device, level), 0.2),
                              borderColor: deviceLevelColor(device, level),
                              border: '1px solid',
                            }}
                          />
                        ))}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant='subtitle2' color='primary' sx={{ mb: 1 }}>
                  <PersonIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                  Nutzer mit diesem Recht ({detail.users?.length ?? 0})
                </Typography>
                <Stack spacing={1}>
                  {(detail.users || []).map(u => (
                    <Paper
                      key={u.userId}
                      variant='outlined'
                      sx={{ p: 1.5, bgcolor: theme => alpha(theme.palette.success.main, 0.04) }}
                    >
                      <Typography variant='body2' fontWeight='medium'>
                        {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.username}
                      </Typography>
                      {u.username && (
                        <Typography variant='caption' color='text.secondary' display='block'>
                          {u.username}
                        </Typography>
                      )}
                      <Stack direction='row' flexWrap='wrap' gap={0.5} sx={{ mt: 0.5 }}>
                        {(u.roles || []).map((role, idx) => (
                          <Box key={idx}>
                            <Typography variant='caption' color='text.secondary'>
                              {role.readableName || role.name}:
                            </Typography>
                            {(role.devices || []).map(({ device, level }) => (
                              <Chip
                                key={`${device}-${level}`}
                                size='small'
                                label={`${DEVICE_LABELS[device] || device} · ${LEVEL_LABELS[level] || level}`}
                                sx={{
                                  ml: 0.5,
                                  bgcolor: alpha(deviceLevelColor(device, level), 0.2),
                                  borderColor: deviceLevelColor(device, level),
                                  border: '1px solid',
                                }}
                              />
                            ))}
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
}

export default RoleRightsOverview;
